import React, { useState } from "react";
import { useRouter } from "next/router";
import qs from "query-string";
/** REDUX */
import { useDispatch, useSelector } from "react-redux";

import { retrieveCart } from "api/requests/retrieveCart";
import { Action, Category, Label } from "constants/events";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import {
  REFRESH_CART_FAILURE,
  REFRESH_CART_REQUEST,
  REFRESH_CART_SUCCESS,
} from "redux/reducers/cart";
import {
  getCartContext,
  getCartSessionId,
  getSelectedForDeliveryOption,
} from "redux/selectors/cart";
import { getCartDispensaryData } from "redux/selectors/cartDispensary";
import { getDeliveryAddress } from "redux/selectors/location";

import Button from "components/botanic/Button";

import ScheduledOrderModal from "../ScheduledOrderModal/ScheduledOrderModal";

type Props = {
  isEmbedded?: boolean;
};

const ProceedToCheckoutButton = ({ isEmbedded }: Props) => {
  const dispensary = useSelector(getCartDispensaryData);
  const [showScheduledOrderModal, setShowScheduledOrderModal] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const cartSessionId = useSelector(getCartSessionId);
  const selectedForDeliveryOption = useSelector(getSelectedForDeliveryOption);
  const deliveryAddress = useSelector(getDeliveryAddress);
  const { publishEvent } = useEventTracker();
  const cartContext = useSelector(getCartContext);
  const domainCountryCode = useDomainCountryCode();

  const refreshCartWithChanges = (
    sessionId: string,
    handleNoChanges: () => void
  ) => {
    dispatch({
      type: REFRESH_CART_REQUEST,
    });

    retrieveCart({
      cartContext: cartContext,
      cartId: sessionId,
      domainCountryCode: domainCountryCode,
      updateOnLoad: true,
    })
      .then((response) => {
        const { cart, changes } = response;
        dispatch({
          cart,
          changes,
          type: REFRESH_CART_SUCCESS,
        });
        if (!changes || !changes.length) {
          handleNoChanges();
        }
      })
      .catch((err) => {
        dispatch({
          error: err.message || "Error with refreshing your cart.",
          type: REFRESH_CART_FAILURE,
        });
      });
  };

  const path = router.asPath;
  const queryParams =
    path && path.includes("?") && path.split("?").length
      ? path.split("?")[1]
      : "";

  const parsedQueryParams = qs.parse(queryParams);
  parsedQueryParams.forDelivery = selectedForDeliveryOption.toString();

  if (selectedForDeliveryOption && isEmbedded) {
    parsedQueryParams.deliveryAddress = JSON.stringify(deliveryAddress);
  }

  const checkoutHref = () => {
    return isEmbedded
      ? `/embedded-pickup/reservation?${qs.stringify(parsedQueryParams)}`
      : `/checkout?${qs.stringify(parsedQueryParams)}`;
  };

  const handleNoChanges = () => router.push(checkoutHref());

  const handleSubmit = () => {
    !isEmbedded &&
      publishEvent({
        action: Action.click,
        category: Category.checkoutPage,
        dispensaryId: dispensary?.id,
        label: Label.confirmOrder,
      });

    refreshCartWithChanges(cartSessionId, handleNoChanges);
  };
  return (
    <>
      {showScheduledOrderModal ? (
        <ScheduledOrderModal
          onDismissed={() => setShowScheduledOrderModal(false)}
          onSubmit={handleSubmit}
        />
      ) : (
        <></>
      )}
      <Button
        onClick={(event: { preventDefault: () => void }) => {
          event.preventDefault();
          if (
            dispensary.scheduledDeliveryEnabled &&
            selectedForDeliveryOption
          ) {
            return setShowScheduledOrderModal(true);
          } else {
            handleSubmit();
          }
        }}
        href={checkoutHref()}
        variant="primary"
        className="text-sm mx-auto"
        data-testid="proceed-to-checkout-button"
      >
        proceed to checkout
      </Button>
    </>
  );
};

export default ProceedToCheckoutButton;
